import { combineEpics } from 'redux-observable';
import {
  concatMap,
  filter,
  from,
  ignoreElements,
  map,
  mergeMap,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';

import { CartModel } from './cart.model';
import module from './index';

import cartProducts from 'domains/cart_products';
import cartProductCreator from 'domains/cart_product_creator';

import { catchAndLogError } from 'utils/common';
import reporter from 'utils/reporter';

import type { Epic } from 'redux-observable';
import type { Cart } from './cart.types';
import type { Action, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'store/wos_store';

const { actions, selectors } = module;

type CartEpic = Epic<Action, Action, RootState>;

function hasCartPayload(action: PayloadAction<any>): action is PayloadAction<Cart> {
  return actions.create.fulfilled.match(action) || actions.get.fulfilled.match(action);
}

/**
 * Check for an existing cart guid
 */
const checkCartEpic: Epic<Action> = (action$) =>
  action$.pipe(
    filter(actions.check.pending.match),
    switchMap(() =>
      from(CartModel.check()).pipe(
        map((guid) => (guid ? actions.get.pending() : actions.check.fulfilled())),
        catchAndLogError(actions.check.rejected())
      )
    )
  );

const createCartEpic: Epic<Action> = (action$) =>
  action$.pipe(
    filter(actions.create.pending.match),
    switchMap(() =>
      from(CartModel.create()).pipe(
        map(actions.create.fulfilled),
        catchAndLogError(actions.create.rejected())
      )
    )
  );

/**
 * Create a cart if we try adding to cart without an existing cart
 */
const createCartForAddToCartEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(cartProductCreator.actions.addToCart.pending.match),
    withLatestFrom(state$),
    filter(([, state]) => !selectors.hasGuid(state)),
    map(() => actions.create.pending())
  );

/**
 * Signals to the cart product creator that we have a cart
 */
const addToCartReadyEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(cartProductCreator.actions.addToCart.pending.match),
    mergeMap(({ payload }) =>
      state$.pipe(
        filter(selectors.hasGuid),
        take(1),
        map(() => cartProductCreator.actions.addToCart.ready(payload))
      )
    )
  );

const addSentryCartGuidTagEpic: Epic<Action> = (action$) =>
  action$.pipe(
    filter(hasCartPayload),
    map(({ payload }) => reporter.setTag('cart_guid', payload.guid)),
    ignoreElements()
  );
/**
 * If we've found an existing cart guid, fetch that cart
 */
const getCartEpic: CartEpic = (action$) =>
  action$.pipe(
    filter(actions.get.pending.match),
    switchMap(() =>
      from(CartModel.get()).pipe(
        map(actions.get.fulfilled),
        catchAndLogError(actions.get.rejected())
      )
    )
  );

/**
 * Make sure cart product IDs match the cart's product_ids array
 */
const updateCartProductIdsEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.get.fulfilled.match),
    map(({ payload }) => payload.product_ids),
    map((ids) => cartProducts.actions.addMany(ids.map((id) => ({ id }))))
  );

const updateCartEpic: CartEpic = (action$) =>
  action$.pipe(
    filter(actions.update.pending.match),
    concatMap(({ payload }) =>
      from(CartModel.update(payload)).pipe(
        map(actions.update.fulfilled),
        catchAndLogError(actions.update.rejected())
      )
    )
  );

const combined = combineEpics(
  checkCartEpic,
  getCartEpic,
  updateCartProductIdsEpic,
  createCartForAddToCartEpic,
  addToCartReadyEpic,
  addSentryCartGuidTagEpic,
  createCartEpic,
  updateCartEpic
);
export default combined;
